import { ClientOnly, createFileRoute } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, lazy } from "react";
import { useTranslation } from "react-i18next";
import { normalizeLang } from "@/i18n";

import { listUnifiedCatalogLite } from "@/lib/catalog.functions";
import { unifiedCatalogQuery } from "@/lib/catalog-query";
import { withTimeout } from "@/lib/with-timeout";
import { Link } from "@tanstack/react-router";
import { Section } from "@/components/site/Section";
import { MountOnView } from "@/components/site/MountOnView";
import { HeroDestinationProvider } from "@/components/hero/hero-destination-state";

import { PresentationPriceContext } from "@/components/site/FinalTourPrice";
import { getPresentationPrices } from "@/lib/presentation-prices.functions";
import { listVerifiedHomeReviews } from "@/lib/home-reviews.functions";
import { VerifiedReviews } from "@/components/home/VerifiedReviews";
import { HomeHero } from "@/components/home/HomeHero";
import { VideoLibrary } from "@/components/video/VideoLibrary";
import { HomeCatalogBoundary } from "@/components/home/HomeCatalogBoundary";
import { WhyBookWithUs } from "@/components/home/WhyBookWithUs";
import { PopularDestinations } from "@/components/home/PopularDestinations";
import { MayaFinalCta } from "@/components/home/MayaFinalCta";
import { SiteHeader } from "@/components/site/SiteHeader";
import { BuenFinHomeSection } from "@/components/site/BuenFinCampaign";

// Heavy below-the-fold pieces — split into their own chunks and loaded
// lazily so the hero ships first.
const SiteFooter = lazy(() =>
  import("@/components/site/SiteFooter").then((m) => ({ default: m.SiteFooter })),
);
const Faq = lazy(() => import("@/components/site/Faq").then((m) => ({ default: m.Faq })));

import { SITE_URL } from "@/lib/og";

const productsQueryFor = (lang: string) =>
  queryOptions({
    queryKey: ["catalog", "unified-lite", lang],
    queryFn: () => listUnifiedCatalogLite({ data: { lang } }),
    staleTime: 5 * 60 * 1000,
  });

export const Route = createFileRoute("/")({
  head: () => {
    const url = "https://www.mostrandomexico.mx/";
    const title = "Vive México con quien lo conoce — Mostrando México";
    const description =
      "Tours con operadores locales en México. Consulta destinos, disponibilidad y montos finales, elige tu hotel y reserva con pago seguro y WhatsApp 24/7.";
    // Imagen corporativa horizontal 1200x630 (fotografía real autorizada +
    // logotipo + claim). Absoluta, pública, HTTPS y versionada.
    const image = `${SITE_URL}/og/og-home-v3.jpg?v=1`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "Mostrando México" },
        { property: "og:locale", content: "es_MX" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { property: "og:image:secure_url", content: image },
        { property: "og:image:type", content: "image/jpeg" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        {
          property: "og:image:alt",
          content: "Mostrando México — Tours y experiencias en México",
        },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
        {
          name: "twitter:image:alt",
          content: "Mostrando México — Tours y experiencias en México",
        },
      ],

      links: [
        { rel: "canonical", href: url },
        // Preload the actual hero LCP image (Maya's universe background).
      ],

      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Mostrando México",
            alternateName: "MostrandoMexico",
            url,
            potentialAction: {
              "@type": "SearchAction",
              target: `${url}tours?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Mostrando México",
            alternateName: "MostrandoMexico",
            url,
            logo: "https://www.mostrandomexico.mx/favicon.png?v=4",
            sameAs: [],
          }),
        },
      ],
    };
  },
  validateSearch: (search: Record<string, unknown>): { orbit?: string } => ({
    orbit: typeof search.orbit === "string" ? (search.orbit as string) : undefined,
  }),
  loader: async ({ context }) => {
    // Prefer real catalog data for SSR, but cap the first-screen wait.
    // Do not put an unresolved query in the SSR cache: serialization may
    // wait for it even when the UI has a Suspense fallback.
    try {
      const lite = await withTimeout(listUnifiedCatalogLite({ data: { lang: context.lang } }), 1000, "home:first-screen");
      context.queryClient.setQueryData(productsQueryFor(context.lang).queryKey, lite);
      context.queryClient.setQueryData(["catalog", "unified", context.lang], lite);
      const ids = lite.tours.filter(t=>t.source === "bokun" && /^\d+$/.test(t.id)).map(t=>t.id).slice(0,200);
      const [prices, reviews] = await Promise.all([
        withTimeout(getPresentationPrices({data:{ids}}), 1000, "home:prices").catch(()=>({})),
        withTimeout(listVerifiedHomeReviews(), 1000, "home:reviews").catch(()=>({reviews:[]})),
      ]);
      for (const [id, price] of Object.entries(prices)) context.queryClient.setQueryData(["tour-card-price",id],price);
      context.queryClient.setQueryData(["home","verified-reviews"],reviews);
      return { catalog: lite, prices };
    } catch {
      // No fabricated rows or prices. The unchanged frame is usable while
      // the client loads the same catalog through its normal query.
      return { catalog: null, prices: {} };
    }
  },
  component: HomePage,
});

function HomePage() {
  const { prices } = Route.useLoaderData();
  return (
    <PresentationPriceContext.Provider value={prices}><HeroDestinationProvider>
      {/* El padding inferior evita que la navegación fija tape contenido. */}
      <div className="relative min-h-screen bg-background pb-[calc(env(safe-area-inset-bottom,0px)+72px)] lg:pb-0">
        {/* Encabezado blanco: ícono + «Mostrando México». */}
        <SiteHeader compactHome />

        {/* 1. Hero: foto real, buscador, destinos y tarjetas con disponibilidad. */}
        <HeroIntro />
        <MountOnView minHeight="0px"><div className="mm-container py-6"><VideoLibrary /></div></MountOnView>

        {/* 2. Destinos (cuadrícula de 4 + «Ver todos»). */}
        <HomeCatalogBoundary resetKey="destinations" fallback={<SectionSkeleton />}>
          <Suspense fallback={<SectionSkeleton />}>
            <ClientOnly fallback={<SectionSkeleton />}><CatalogSections /></ClientOnly>
          </Suspense>
        </HomeCatalogBoundary>

        {/* 3. Por qué reservar con nosotros. */}
        <BuenFinHomeSection />
        <WhyBookWithUs />
        <VerifiedReviews />

        {/* 4. Tarjeta de Maya. */}
        <MayaFinalCta />

        {/* 11. Footer. */}
        <MountOnView minHeight="300px">
          <Suspense fallback={null}>
            <SiteFooter />
          </Suspense>
        </MountOnView>
      </div>
    </HeroDestinationProvider></PresentationPriceContext.Provider>
  );
}

/**
 * Destinos populares y tours más reservados comparten una sola consulta del
 * catálogo: no se duplican peticiones ni productos.
 */
function CatalogSections() {
  const { i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const { data } = useSuspenseQuery(productsQueryFor(lang));

  return (
    <>
      <PopularDestinations tours={data.tours} />
    </>
  );
}

function HeroIntro() {
  const { catalog } = Route.useLoaderData();
  const { i18n } = useTranslation();
  const fallback = <HomeHero tours={catalog?.tours ?? []} />;
  return <HomeCatalogBoundary resetKey={i18n.language} fallback={fallback}>
    <ClientOnly fallback={fallback}><Suspense fallback={fallback}>
      <LiveHomeHero />
    </Suspense></ClientOnly>
  </HomeCatalogBoundary>;
}

function LiveHomeHero() {
  const { i18n } = useTranslation();
  const { data } = useSuspenseQuery(productsQueryFor(normalizeLang(i18n.language)));
  return <HomeHero tours={data.tours} />;
}

function FaqSection() {
  const { t } = useTranslation();
  return (
    <Section eyebrow={t("homeV2.faqEyebrow")} title={t("homeV2.faqTitle")} tone="sand">
      <Faq />
    </Section>
  );
}

function HeroFallback() {
  // Esqueleto del hero: mismos bloques que el contenido real para que la
  // primera pantalla se vea viva y no como una pantalla en blanco.
  return (
    <div className="relative min-h-[100svh] overflow-hidden bg-[#FFFDF7]">
      <div className="absolute inset-0 animate-pulse bg-[radial-gradient(45%_35%_at_50%_38%,rgba(217,153,51,0.16),transparent_70%)]" />
      <div className="relative mx-auto flex min-h-[100svh] w-full max-w-6xl flex-col justify-center gap-6 px-6 py-24">
        <div className="h-3 w-40 animate-pulse rounded-full bg-amber-200/50" />
        <div className="h-10 w-3/4 animate-pulse rounded-2xl bg-black/5 sm:h-14" />
        <div className="h-4 w-2/3 animate-pulse rounded-full bg-black/5" />
        <div className="mt-2 flex flex-wrap gap-2">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="h-8 w-24 animate-pulse rounded-full bg-black/5" />
          ))}
        </div>
      </div>
      <span className="sr-only">Cargando</span>
    </div>
  );
}

function SectionSkeleton() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="aspect-[4/3] animate-pulse rounded-2xl bg-muted" />
        ))}
      </div>
    </div>
  );
}
